import { FaceDetectionResult } from "./FaceDetector";

/**
 * Passive natural-motion liveness detector.
 *
 * Replaces the earlier sequential "do BLINK, then TURN_HEAD, then OPEN_MOUTH"
 * choreography with a single continuous scan window: the user is simply told
 * to "blink and look around naturally" while the detector watches for signs
 * of a live face (a genuine blink, or natural head movement) without
 * commanding specific actions in order. This mirrors a passive liveness scan
 * rather than a step-by-step obstacle course.
 *
 * Pass criteria: at least one genuine blink OR a natural head-yaw excursion
 * detected at any point during the window — whichever happens first, since a
 * real person glancing around naturally will trigger one or both without
 * being told to.
 */

const BLINK_THRESHOLD = 0.25;
const BLINK_HOLD_FRAMES = 2;
const YAW_MOTION_THRESHOLD_DEG = 6; // small natural head movement, not a commanded turn

export interface ScanProgress {
  faceDetected: boolean;
  blinkDetected: boolean;
  motionDetected: boolean;
  livenessConfirmed: boolean;
}

export class ActionDetector {
  private blinkHoldCount = 0;
  private blinkDetected = false;
  private motionDetected = false;
  private baselineYaw: number | null = null;

  check(result: FaceDetectionResult): ScanProgress {
    if (!result.faceDetected) {
      return this.buildProgress(false);
    }

    const bs = result.blendshapes;
    const blinkConf = ((bs.get("eyeBlinkLeft") || 0) + (bs.get("eyeBlinkRight") || 0)) / 2;

    if (!this.blinkDetected) {
      if (blinkConf >= BLINK_THRESHOLD) {
        this.blinkHoldCount++;
        if (this.blinkHoldCount >= BLINK_HOLD_FRAMES) {
          this.blinkDetected = true;
        }
      } else {
        this.blinkHoldCount = 0;
      }
    }

    if (!this.motionDetected) {
      const yaw = this.getHeadYaw(result.transformMatrix);
      if (this.baselineYaw === null) {
        this.baselineYaw = yaw;
      } else if (Math.abs(yaw - this.baselineYaw) >= YAW_MOTION_THRESHOLD_DEG) {
        this.motionDetected = true;
      }
    }

    return this.buildProgress(true);
  }

  private buildProgress(faceDetected: boolean): ScanProgress {
    return {
      faceDetected,
      blinkDetected: this.blinkDetected,
      motionDetected: this.motionDetected,
      livenessConfirmed: this.blinkDetected || this.motionDetected,
    };
  }

  private getHeadYaw(matrix: number[] | null): number {
    if (!matrix || matrix.length < 16) return 0;
    return Math.atan2(-matrix[2], matrix[0]) * (180 / Math.PI);
  }
}
